import { minutesToMilliseconds } from 'date-fns';
import { useCallback, useRef } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Button } from '@growtherapy/sprout-ui';

import {
  ClientInformationDrawerTab as SeedlingClientInformationDrawerTab,
  useGetPatientNextAppointment,
  useSetActiveClientInfoDrawerTab,
} from '@grow-therapy-team/seedling-components';

import { logger } from '../../datadog/logger';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useEndOfSessionToast } from '../../hooks/useEndOfSessionToast';
import { Translate } from '../../i18n/Translate';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { toast } from '../../toasts';
import { roomAtom } from '../../video-sdk';
import { useOpenClientInformationCallback } from '../client-information/useClientInformationDrawerCallbacks';
import { AppointmentType } from '../schedule-preview/types';
import { ProviderDrawerState } from '../state';
import { useCachedPatientByShortId } from '../useGetPatientByShortId';
import { PromptFollowUpToast } from './PromptFollowUpToast';
import { useFollowUpNudgeExperiment } from './useFollowUpNudgeExperiment';
import { useInSessionAppointment } from './useInSessionAppointment';
import { formatNextAppointmentDateTime, hasScheduledFollowUp } from './utils';

const NUDGE_PRIOR_END_TIME_MS = minutesToMilliseconds(5);

export function usePromptFollowUpToast(
  providerShortId: string | undefined,
  inSessionPatientInformation?: {
    patientShortId: string;
    visitorUuid?: string;
  },
) {
  const t = useTranslate();
  const getIsInSession = useAtomCallbackMemoized((get) => !!get(roomAtom), []);
  const { patientShortId, visitorUuid } = inSessionPatientInformation ?? {};
  const setSeedlingActiveTab = useSetActiveClientInfoDrawerTab();
  const { userClicked, userViewed } = useTrackEvent();

  const openClientInformation = useOpenClientInformationCallback();
  const inSessionAppointment = useInSessionAppointment();
  const isIntakeAppointment = inSessionAppointment?.appointmentType === AppointmentType.Intake;
  const isFollowUpAppointment = inSessionAppointment?.appointmentType === AppointmentType.FollowUp;
  const shouldSeeFollowUpNudge = useFollowUpNudgeExperiment(isFollowUpAppointment);
  const { data: patientChartData } = useCachedPatientByShortId(patientShortId);
  const { data: nextAppointmentData, refetch: refetchNextAppointment } = useGetPatientNextAppointment(patientShortId);

  const patientChart = patientChartData?.patientChart;
  const clientFirstName = patientChart?.firstName ?? '';
  const appointmentEndTime = inSessionAppointment?.timeEnd;
  const appointmentShortId = inSessionAppointment?.appointmentShortId ?? '';

  // The cached chart is prefetched in the waiting room, where its next appointment can still be the session being
  // joined, so the next appointment is refetched just before the toast is shown.
  const nextAppointment = nextAppointmentData
    ? nextAppointmentData.patientChart?.nextAppointment
    : patientChart?.nextAppointment;
  const hasFollowUpScheduled = hasScheduledFollowUp(nextAppointment, appointmentEndTime);

  const isRebookingNudge = isIntakeAppointment || shouldSeeFollowUpNudge;
  const isRebookingNudgeRef = useRef(isRebookingNudge);
  isRebookingNudgeRef.current = isRebookingNudge;

  const sessionType = isIntakeAppointment ? ('intake' as const) : ('followup' as const);
  const sessionTypeRef = useRef(sessionType);
  sessionTypeRef.current = sessionType;

  const isFollowUpAppointmentRef = useRef(isFollowUpAppointment);
  isFollowUpAppointmentRef.current = isFollowUpAppointment;

  const hasFollowUpScheduledRef = useRef(hasFollowUpScheduled);
  hasFollowUpScheduledRef.current = hasFollowUpScheduled;

  const nextAppointmentRef = useRef(nextAppointment);
  nextAppointmentRef.current = nextAppointment;

  const clientFirstNameRef = useRef(clientFirstName);
  clientFirstNameRef.current = clientFirstName;

  const openAppointments = useCallback(() => {
    if (!patientShortId) {
      logger.error('Missing patient short ID when opening client information drawer (usePromptFollowUpToast)');
      return;
    }
    openClientInformation({ patientShortId, visitorUuid });
    setSeedlingActiveTab(SeedlingClientInformationDrawerTab.APPOINTMENTS);
  }, [openClientInformation, patientShortId, setSeedlingActiveTab, visitorUuid]);

  const openSchedulingDrawer = useCallback(() => {
    if (!patientShortId) {
      logger.error('Missing patient short ID when opening scheduling drawer (usePromptFollowUpToast)');
      return;
    }
    void openClientInformation({ patientShortId, visitorUuid }, ProviderDrawerState.ADD_APPOINTMENT);
  }, [openClientInformation, patientShortId, visitorUuid]);

  const openAppointmentsRef = useRef(openAppointments);
  openAppointmentsRef.current = openAppointments;

  const openSchedulingDrawerRef = useRef(openSchedulingDrawer);
  openSchedulingDrawerRef.current = openSchedulingDrawer;

  const appointmentShortIdRef = useRef(appointmentShortId);
  appointmentShortIdRef.current = appointmentShortId;

  const getToastTrackingParams = () => ({
    appointmentShortId: appointmentShortIdRef.current,
    patientShortId: patientShortId ?? '',
    providerShortId: providerShortId ?? '',
    sessionType: sessionTypeRef.current,
    experimentGroup: String(isRebookingNudgeRef.current),
  });

  const getRebookingNudgeParams = () => ({
    ...getToastTrackingParams(),
    hasFutureAppointment: hasFollowUpScheduledRef.current,
  });

  const trackSchedulingPrompt = () => {
    if (!isFollowUpAppointmentRef.current) return;
    userClicked(EventTypeEnum.FIVE_MINUTE_REMINDER_TOAST_SCHEDULING_PROMPT, getToastTrackingParams());
  };
  const trackSchedulingPromptRef = useRef(trackSchedulingPrompt);
  trackSchedulingPromptRef.current = trackSchedulingPrompt;

  const refreshNextAppointment = async () => {
    if (!patientShortId || !appointmentEndTime) return;
    try {
      const { data } = await refetchNextAppointment();
      if (!data) return;

      const appointment = data.patientChart?.nextAppointment;
      nextAppointmentRef.current = appointment;
      hasFollowUpScheduledRef.current = hasScheduledFollowUp(appointment, appointmentEndTime);
    } catch (error) {
      logger.error('Unable to refetch next appointment (usePromptFollowUpToast)', { patientShortId }, error as Error);
    }
  };

  const showToast = async () => {
    const isRebookingNudgeSession = isRebookingNudgeRef.current;
    if (isRebookingNudgeSession) {
      await refreshNextAppointment();
    }

    const dismissedByUserRef = { current: false };

    if (isRebookingNudgeSession) {
      userViewed(EventTypeEnum.TELEHEALTH_REBOOKING_NUDGE_SHOWN, getRebookingNudgeParams());
    } else if (isFollowUpAppointmentRef.current) {
      userViewed(EventTypeEnum.FIVE_MINUTE_REMINDER_TOAST_SHOWN, getToastTrackingParams());
    }

    toast.enqueue(
      ({ closeToast, ...renderProps }) => {
        const ariaProps = {
          ...renderProps,
          closeButtonProps: {
            ...renderProps.closeButtonProps,
            onClick: () => {
              dismissedByUserRef.current = true;
              closeToast();
            },
          },
        };

        if (isRebookingNudgeRef.current) {
          if (hasFollowUpScheduledRef.current && nextAppointmentRef.current?.timeStart) {
            const formattedDateTime = formatNextAppointmentDateTime(String(nextAppointmentRef.current.timeStart));

            return (
              <PromptFollowUpToast
                {...ariaProps}
                variant='rebookingNudge'
                title={t('telehealth-core:promptFollowUpToast.intakeFollowUpScheduledTitle')}
                subtitle={t('telehealth-core:promptFollowUpToast.intakeFollowUpScheduledSubtitle', {
                  formattedDateTime,
                })}
              />
            );
          }

          return (
            <PromptFollowUpToast
              {...ariaProps}
              variant='rebookingNudge'
              title={t('telehealth-core:promptFollowUpToast.intakeNoFollowUpTitle')}
              subtitle={t('telehealth-core:promptFollowUpToast.intakeNoFollowUpSubtitle', {
                clientFirstName: clientFirstNameRef.current,
              })}
              callToAction={t('telehealth-core:promptFollowUpToast.scheduleAppointmentCta')}
              onCallToAction={() => {
                closeToast();
                openSchedulingDrawerRef.current();
                userClicked(EventTypeEnum.TELEHEALTH_REBOOKING_NUDGE_CLICKED, {
                  ...getRebookingNudgeParams(),
                  ctaType: 'schedule',
                });
              }}
            />
          );
        }

        return (
          <PromptFollowUpToast
            {...ariaProps}
            title={t('telehealth-core:promptFollowUpToast.fiveMinuteReminderTitle')}
            subtitle={
              <Translate
                i18nKey='telehealth-core:promptFollowUpToast.fiveMinuteReminderSubtitle'
                components={{
                  1: (
                    <Button
                      onClick={() => {
                        closeToast();
                        openAppointmentsRef.current();
                        trackSchedulingPromptRef.current();
                      }}
                      use='unstyled'
                      className='underline decoration-neutral-600 inline p-0 arc:p-0 arc:min-h-[unset]'
                      size='sm'
                    />
                  ),
                }}
              />
            }
          />
        );
      },
      {
        onClose: () => {
          if (!dismissedByUserRef.current) return;

          if (isRebookingNudgeSession) {
            userClicked(EventTypeEnum.TELEHEALTH_REBOOKING_NUDGE_DISMISSED, getRebookingNudgeParams());
          } else if (isFollowUpAppointmentRef.current) {
            userClicked(EventTypeEnum.FIVE_MINUTE_REMINDER_TOAST_DISMISSED, getToastTrackingParams());
          }
        },
        purpose: 'prompt',
        timeout: minutesToMilliseconds(1),
      },
    );
  };

  useEndOfSessionToast(appointmentEndTime, showToast, NUDGE_PRIOR_END_TIME_MS, getIsInSession());
}
